import React, { useRef, useCallback, useMemo } from "react";
import styles from "./RangeSlider.module.css";

/**
 * @param {Object} props
 * @param {number} [props.min=0] - Minimum value.
 * @param {number} [props.max=100] - Maximum value.
 * @param {number[]} props.value - One or two values for single/dual thumb.
 * @param {(values: number[]) => void} [props.onChange]
 * @param {number} [props.step=1]
 * @param {boolean | { value: number, label?: string }[]} [props.marks=false]
 * @param {boolean} [props.disabled=false]
 * @param {string[]} [props.thumbColors=['#57abff', '#F25146']] - Color per thumb, in order.
 * @param {boolean} [props.showValues=true] - Show current value inside each thumb.
 */
const RangeSlider = ({
  min = 0,
  max = 100,
  value = [0],
  onChange,
  step = 1,
  marks = false,
  disabled = false,
  thumbColors = ["#F25146", "#57abff"],
  showValues = true,
}) => {
  const railRef = useRef(null);
  const draggingIndex = useRef(null);

  // Keep latest props in a ref so stable callbacks always read current values
  const live = useRef({});
  live.current = { value, onChange, min, max, step };

  const valueToPercent = useCallback(
    (val) => {
      return ((val - min) / (max - min)) * 100;
    },
    [min, max],
  );

  const getValueFromClientX = (clientX) => {
    const { min, max, step } = live.current;
    const rail = railRef.current;
    if (!rail) {
      return min;
    }
    const rect = rail.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(100, ((clientX - rect.left) / rect.width) * 100),
    );
    const raw = (percent / 100) * (max - min) + min;
    return Math.max(min, Math.min(max, Math.round(raw / step) * step));
  };

  // Stable refs break the closure cycle for document event listeners
  const stableMoveRef = useRef(null);
  const stableUpRef = useRef(null);
  const stableMove = useCallback((ev) => stableMoveRef.current?.(ev), []);
  const stableUp = useCallback(() => stableUpRef.current?.(), []);

  stableMoveRef.current = (ev) => {
    if (draggingIndex.current === null) {
      return;
    }
    const { value, onChange } = live.current;
    if (!onChange) {
      return;
    }
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
    const newVal = getValueFromClientX(clientX);
    const next = [...value];
    next[draggingIndex.current] = newVal;
    if (next.length === 2) {
      if (draggingIndex.current === 0 && next[0] > next[1]) {
        next[0] = next[1];
      }
      if (draggingIndex.current === 1 && next[1] < next[0]) {
        next[1] = next[0];
      }
    }
    onChange(next);
  };

  stableUpRef.current = () => {
    draggingIndex.current = null;
    document.removeEventListener("mousemove", stableMove);
    document.removeEventListener("mouseup", stableUp);
    document.removeEventListener("touchmove", stableMove);
    document.removeEventListener("touchend", stableUp);
  };

  const handleThumbMouseDown = useCallback(
    (index) => (ev) => {
      if (disabled) {
        return;
      }
      ev.preventDefault();
      draggingIndex.current = index;
      document.addEventListener("mousemove", stableMove);
      document.addEventListener("mouseup", stableUp);
    },
    [disabled, stableMove, stableUp],
  );

  const handleThumbTouchStart = useCallback(
    (index) => (ev) => {
      if (disabled) {
        return;
      }
      draggingIndex.current = index;
      document.addEventListener("touchmove", stableMove);
      document.addEventListener("touchend", stableUp);
    },
    [disabled, stableMove, stableUp],
  );

  const handleThumbKeyDown = useCallback(
    (index) => (ev) => {
      const { value, onChange, min, max, step } = live.current;
      if (disabled || !onChange) {
        return;
      }
      let delta = 0;
      if (ev.key === "ArrowRight" || ev.key === "ArrowUp") {
        delta = step;
      }
      if (ev.key === "ArrowLeft" || ev.key === "ArrowDown") {
        delta = -step;
      }
      if (!delta) {
        return;
      }
      ev.preventDefault();
      const next = [...value];
      next[index] = Math.max(min, Math.min(max, next[index] + delta));
      if (next.length === 2) {
        if (index === 0 && next[0] > next[1]) next[0] = next[1];
        if (index === 1 && next[1] < next[0]) next[1] = next[0];
      }
      onChange(next);
    },
    [disabled],
  );

  const generatedMarks = useMemo(() => {
    if (!marks) {
      return [];
    }
    if (Array.isArray(marks)) {
      return marks;
    }
    const count = (max - min) / step;
    if (count > 100) {
      return [];
    }
    return Array.from({ length: count + 1 }, (_, i) => ({
      value: min + i * step,
    }));
  }, [marks, min, max, step]);

  const trackStart = value.length >= 2 ? valueToPercent(value[0]) : 0;
  const trackEnd =
    value.length >= 2 ? valueToPercent(value[1]) : valueToPercent(value[0]);
  const hasMarkLabels = generatedMarks.some((m) => m.label);

  return (
    <div
      className={`${styles.container}${disabled ? ` ${styles.disabled}` : ""}`}
    >
      <div className={styles.rail} ref={railRef}>
        <div
          className={styles.track}
          style={{ left: `${trackStart}%`, width: `${trackEnd - trackStart}%` }}
        />

        {generatedMarks.map((mark) => (
          <span
            key={mark.value}
            className={styles.mark}
            style={{ left: `${valueToPercent(mark.value)}%` }}
          />
        ))}

        {value.map((val, i) => {
          const color = thumbColors[i] ?? thumbColors[thumbColors.length - 1];
          return (
            <div
              key={i}
              className={styles.thumbWrapper}
              style={{ left: `${valueToPercent(val)}%` }}
              onMouseDown={handleThumbMouseDown(i)}
              onTouchStart={handleThumbTouchStart(i)}
              onKeyDown={handleThumbKeyDown(i)}
              tabIndex={disabled ? -1 : 0}
              role="slider"
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={val}
            >
              <div
                className={styles.thumb}
                style={{
                  backgroundColor: color,
                  border: `1px solid ${color}`,
                  boxShadow: `0 0 2px 1px ${color}`,
                }}
              >
                {showValues && `${val}°`}
              </div>
            </div>
          );
        })}
      </div>

      {hasMarkLabels && (
        <div className={styles.markLabels}>
          {generatedMarks
            .filter((m) => m.label)
            .map((mark) => (
              <span
                key={mark.value}
                className={styles.markLabel}
                style={{ left: `${valueToPercent(mark.value)}%` }}
              >
                {mark.label}
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
